import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import minBy from 'lodash/minBy';
import sortedIndexBy from 'lodash/sortedIndexBy';
import {
  blueLightRule,
  cleanRule,
  filterExistingProperties,
  getFrameOnlyKey,
  parseOptionValue,
  sunRule,
  transitionRule,
} from '.';
import { configObjectToSliderObject } from '../../adapters/ConfigObjectToSliderObject';
import {
  Content,
  ContentEntry,
  Entry,
  LensPackage,
  Option,
  Prescription,
} from '../../meta/IBrain';
import { Parameter, ParameterWithTitle } from '../../models/Parameter';
import { Step } from '../../models/Step';
import { colorActions } from '../../redux/slices/color';
import {
  IncludedTreatment,
  LensColorModel,
  LensColorPromo,
  StepCardProps,
} from '../types';
import {
  getCheapPackageFromList,
  getCheapestPackages,
  getPriceFromPackageAndCustomFiltered,
  getPriceStep,
} from './prices';

export function getContentOptions(content?: Content, keyName?: string) {
  let options: Entry[] | [] = [];
  if (!content) {
    throw new Error('content is not available');
  }

  const key = Object.keys(content).find(
    (el) => el.toLowerCase() === keyName?.toLowerCase()
  );
  if (key) {
    const entries: ContentEntry[] = content[key];
    if (entries) {
      options = Object.entries(entries)
        .map(([key, val]) => ({ id: key, ...val }))
        .sort((a, b) => a.sequence - b.sequence);
    }
  }
  return options;
}
export enum StepBadgeType {
  BEST_SELLER,
  BEST_PRESCRIPTION,
  MOST_POPULAR,
  IS_NEW,
  CUSTOM,
  RECOMMENDED,
  COMING_SOON,
}
export const getStepBadgeType = (badge: string) => {
  if (!badge) {
    return null;
  }
  switch (badge?.toLowerCase()) {
    case 'bestseller':
      return StepBadgeType.BEST_SELLER;
    case 'best prescription':
      return StepBadgeType.BEST_PRESCRIPTION;
    case 'most popular':
      return StepBadgeType.MOST_POPULAR;
    case 'new':
      return StepBadgeType.IS_NEW;
    case 'recommended':
      return StepBadgeType.RECOMMENDED;
    case 'coming soon':
      return StepBadgeType.COMING_SOON;
    default:
      return StepBadgeType.CUSTOM;
  }
};

export function isMobile() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileWidth = window.innerWidth <= 1024;

  // Checks for iOS devices
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true;
  }

  // Checks for Android devices
  if (/android/i.test(userAgent)) {
    return true;
  }

  // Check if width of device is smaller then 1024px
  if (isMobileWidth) {
    return true;
  }

  // If neither iOS nor Android, it's probably not a mobile device
  return false;
}

function useMoreInfo(optionKey: string, content?: Content) {
  const hasMoreInfo = (optionValue: any, content, optionKeyFamily) => {
    return (
      content &&
      content[optionKeyFamily || optionKey] &&
      content[optionKeyFamily || optionKey][optionValue] &&
      content[optionKeyFamily || optionKey][optionValue].contentIdentifier
    );
  };
  const getMoreInfo = (optionValue: any, content, optionKeyFamily) =>
    (!isMobile() ? 'DESK_' : 'MOB_') +
    content[optionKeyFamily || optionKey][optionValue].contentIdentifier;
  // const setFocus = (id: string) => {
  //   reduxDispatch(modalsActions.setOpenLearnMoreModal(id));
  // };

  const findFamily = (title: string, content) => {
    let family = content['treatmentFamily'];
    let finded = null;
    if (family) {
      Object.keys(family).forEach((e) => {
        if (family[e].title == title) {
          finded = e;
        }
      });
    }
    return finded;
  };

  const getMoreInfoList = (options: any[]) => {
    let moreInfoList = [];
    const mapped = options.map((option) => {
      let optionKeyFamily = null;
      let optionValue = option.id;
      if (optionKey === 'treatment' || optionKey === 'antiReflective') {
        //is GVP
        optionValue = optionValue.split('$')[1];
      }

      if (optionKey === 'treatmentsFamily') {
        let splitted = optionValue.split('$');
        optionKeyFamily = splitted.length > 1 ? splitted[0] : 'treatmentFamily';
        optionValue = splitted.length > 1 ? splitted[1] : option.id;
        if (splitted.length == 1) {
          let finded = findFamily(option.title, content);
          optionValue = finded ? finded : optionValue;
        }
      }

      if (hasMoreInfo(optionValue, content, optionKeyFamily)) {
        const moreInfo = getMoreInfo(optionValue, content, optionKeyFamily);
        option.contentIdentifier = hasMoreInfo(
          optionValue,
          content,
          optionKeyFamily
        );
        moreInfoList.push(moreInfo);
        option.moreInfo = (setOpenLearnMoreModal: (id: string) => void) =>
          setOpenLearnMoreModal(moreInfo);
      }
      return option;
    });
    window.dispatchEvent(
      new CustomEvent('setListLearnMoreModal', {
        detail: JSON.parse(JSON.stringify(moreInfoList)),
      })
    );
    return mapped;
  };

  return getMoreInfoList;
}

function sortingGreyOut(options: any) {
  return options.sort((a: any, b: any) =>
    a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
  );
}

////////////////////////////////////////////////////////////////

// bundles

export function getBundles(config: any, currentPackages?: LensPackage[]) {
  const configBundles = getContentOptions(
    config.lensesData.content,
    'lensbundle'
  );

  return configBundles
    .sort((a, b) =>
      a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
    )
    .map((bundle) => {
      const priceObj = bundle.disabled
        ? null
        : getPriceStep({ lensBundle: bundle.id }, currentPackages);
      const minPackageFilter = getCheapestPackages(currentPackages ?? [], {
        bundle: bundle,
      })?.minPackageFilterPrice;
      if (minPackageFilter) {
        window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
      }
      const filteredPackages = currentPackages?.filter((s) =>
        filterExistingProperties(s.lensPackage, { lensBundle: bundle.id })
      );
      const speedyRxNotCompatible = filteredPackages?.every(
        (p) => !p.lensPackage.isSpeedyRX
      );
      return {
        ...bundle,
        ...priceObj,
        textBadge: bundle.badge,
        imageUrl: bundle.imageFileName,
        badge: bundle.badge ? getStepBadgeType(bundle.badge) : null,
        disabled: bundle.disabled || bundle.badge == 'coming soon',
        speedyRxNotCompatible: speedyRxNotCompatible,
      };
    });
}

// type
export const getTypes = (
  config: any,
  initialLensPackages?: LensPackage[],
  currentPackages?: LensPackage[],
  currentPrescription?: Prescription,
  activateDistanceAndReading?: boolean,
  parentSelections?: any
) => {
  const content = config?.lensesData?.content;
  if (!config || !initialLensPackages || !currentPackages) return [];
  let contentTypes: Entry[] = getContentOptions(content, 'type');
  const showFrameOnlyFromConfig =
    config?.data?.showFrameOnly ??
    window.rxcBrain.flowDefiningAttributes?.showFrameOnly;

  // Se non c'è "Frame Only" dentro lo aggiungiamo noi
  if (showFrameOnlyFromConfig) {
    if (
      contentTypes.length &&
      !contentTypes.some((ct) => ct.id === 'Frame Only')
    ) {
      contentTypes.push({
        id: 'Frame Only',
        sequence: 99,
        title: 'Frame Only',
        description: 'MISSING CONTENT',
        categoryTypes: contentTypes[0]?.categoryTypes,
      });
    }
  }

  const extendedTypeContent = contentTypes.map((content) => {
    const filteredPackages = currentPackages?.filter((s) =>
      filterExistingProperties(s.lensPackage, { type: content.id })
    );
    let speedyRxNotCompatible = false;
    if (content.id === 'Distance' || content.id === 'Reading') {
      const contentSingleVisionID = contentTypes.find(
        (c) => c.id?.toLowerCase() === 'single vision'
      )?.id;
      const filteredPackagesSV = currentPackages?.filter((s) =>
        filterExistingProperties(s.lensPackage, { type: contentSingleVisionID })
      );
      speedyRxNotCompatible = filteredPackagesSV?.every(
        (p) => !p.lensPackage.isSpeedyRX
      );
    } else {
      speedyRxNotCompatible = filteredPackages?.every(
        (p) => !p.lensPackage.isSpeedyRX
      );
    }
    return {
      ...content,
      imageUrl: content.imageFileName,
      noMarginTitle: true,
      badge: content.badge && getStepBadgeType(content.badge as string),
      textBadge: content.badge ? content.badge : '',
      disabled: content.badge == 'coming soon' ? true : false,
      moreInfo:
        config.brand === 'clearly' && content.contentIdentifier
          ? config.actionsModule.learnMoreContent
          : false,
      // speedyRxNotCompatible: content.id === 'Progressive',

      speedyRxNotCompatible: speedyRxNotCompatible,
      hideWithSpeedyActive:
        content.id === 'Frame Only' || content.id === 'Non-prescription',
      // onClick: () =>
      //   content.badge !== "coming soon" &&
      //   !content.disabled &&
      //   onTypeSelected(content.id),
    };
  });

  let types = extendedTypeContent
    .filter((content) => {
      return currentPackages?.some(
        (cp: LensPackage) => cp.lensPackage.type === content.id
      );
    })
    .filter((content) => {
      if (parentSelections && parentSelections?.['type']?.values) {
        const parentTypes =
          parentSelections['type'].values.map((v) => v.id) ?? [];
        return parentTypes.includes(content.id);
      } else return true;
    })
    //filter frameOnly
    .filter((content) => {
      const showFrameOnly = showFrameOnlyFromConfig == false ? false : true;

      const configFrameOnly = getFrameOnlyKey(initialLensPackages, config.data);

      if (content.id !== configFrameOnly) return true;
      return showFrameOnly;
    });

  types.forEach((type) => {
    const minPackageFilter = getCheapestPackages(currentPackages, {
      type: type.id,
    })?.minPackageFilterPrice;
    if (minPackageFilter) {
      window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
    }
  });

  if (window.rxcBrain?.showFramePlusLensesPrice) {
    types = types.map((type) => {
      let priceObj = getPriceStep({ type: type.id }, currentPackages);
      if (type.id === 'Frame Only') {
        priceObj = {
          price: config.data.frame.offerPrice,
          discount: 0,
          insPrice: 0,
        };
        return {
          ...type,
          ...priceObj,
          ignorePriceLabels: true,
        };
      }
      return {
        ...type,
        ...priceObj,
      };
    });
  }

  const prescriptionStep = window.rxcBrain.stepChain.find(
    (step) => step.key === 'LensPrescription'
  );
  const isPrescriptionStepBefore =
    window.rxcBrain.currentStep?.progressive > prescriptionStep?.progressive;

  if (
    isPrescriptionStepBefore &&
    currentPrescription &&
    (!currentPrescription?.ADD?.OD ||
      !currentPrescription?.ADD?.OS ||
      !currentPrescription?.ADD_OD ||
      !currentPrescription?.ADD_OS)
  ) {
    types = types.filter((type) => type?.id !== 'Progressive');
  }

  if (!activateDistanceAndReading) return types;

  const reading = extendedTypeContent.find(
    (content) => content.id === 'Reading'
  );
  const distance = extendedTypeContent.find(
    (content) => content.id === 'Distance'
  );
  //looking for reading and distance in content
  if (!reading || !distance) {
    return types;
  }

  return (
    ([distance, reading] as { [key: string]: any }[])
      .concat(...types)
      //remove single vision
      .filter((type) => !type.id.toLowerCase().includes('single'))
      .sort((a, b) =>
        a.sequence > b.sequence ? 1 : b.sequence > a.sequence ? -1 : 0
      )
  );
};

// treatments
export const getFamilyFilter = (currentStep: Step) => {
  return currentStep?.params?.find((p) => p.key === 'familyFilter');
};

export const isPhotocromic = ({
  content,
  allCurrentPackages,
  familyFilter,
  fromTreatments,
  dispatch,
}: {
  content?: Content;
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  familyFilter: any;
  fromTreatments?: boolean;
  dispatch?: any;
}) => {
  const setColor = (colorId: string) => {
    const contentColors = getContentOptions(content, 'color');
    if (!colorId) {
      dispatch?.(colorActions.selectTransition(null));
    } else {
      const colorFind = contentColors.find((c) => c.id === colorId);
      const imageFileName = colorFind.imageFileName1
        ? colorFind.imageFileName1
        : colorFind.imageFileName;
      if (colorFind) dispatch?.(colorActions.selectTransition(imageFileName));
    }
  };
  const filterPackagesByFamily = (
    packages: any[],
    requiredoptions: string[],
    excludedoptions?: string[]
  ) => {
    let filteredPackages = packages;
    if (requiredoptions?.length) {
      filteredPackages = filteredPackages.filter((cp) => {
        let ret = true;
        requiredoptions.forEach(
          (t) => (ret = ret && cp.lensPackage[t] != null)
        );
        if (excludedoptions?.length)
          excludedoptions.forEach(
            (t) => (ret = ret && cp.lensPackage[t] === null)
          );
        return ret;
      });
    }
    return filteredPackages;
  };
  const checkFromTreatments = () => {
    let ret = false;
    let packageList = allCurrentPackages?.validRXPackages;
    let greyOutPackageList = allCurrentPackages?.invalidRXPackages;
    if (familyFilter) {
      packageList = filterPackagesByFamily(
        allCurrentPackages?.validRXPackages,
        familyFilter.value?.required,
        familyFilter.value?.excluded
      );

      greyOutPackageList = filterPackagesByFamily(
        allCurrentPackages?.invalidRXPackages,
        familyFilter.value?.required,
        familyFilter.value?.excluded
      );
    }

    let { treatments } = loadTreatments(
      content,
      { validRXPackages: packageList, invalidRXPackages: [] },
      false,
      ['transition']
    );

    if (treatments && treatments.length === 1) {
      const transitionOptions = groupBy(
        packageList.filter((cp: LensPackage) => cp.lensPackage.transition),
        'lensPackage.transition'
      );
      if (Object.keys(transitionOptions).length === 1) {
        const valueTransition = Object.keys(transitionOptions)[0];
        const transitionPackages = packageList.filter(
          (cp: LensPackage) => cp.lensPackage.transition === valueTransition
        );
        const greyOutTransitionPackages = greyOutPackageList.filter(
          (cp: LensPackage) => cp.lensPackage.transition === valueTransition
        );
        const groupedByColor = groupBy(
          transitionPackages.concat(greyOutTransitionPackages),
          'lensPackage.color'
        );
        if (Object.keys(groupedByColor).length === 1) {
          //there is just on color
          //is photocromic
          ret = true;
          setColor(Object.keys(groupedByColor)[0]);
        }
      }
    }

    return ret;
  };
  const checkFromTreatmentsFamily = () => {
    const options = getTreatmentsFamily(
      content,
      allCurrentPackages,
      getCheapPackageFromList(allCurrentPackages?.validRXPackages)
    );
    let ret = false;
    if (options) {
      const onlyTransition = options.filter(
        (p) => p.id.indexOf('transition$') >= 0
      );
      if (options.length === 1 && onlyTransition.length === 1) {
        //there is only one transition
        const valueTransition = onlyTransition[0].id.split('$')[1];
        const transitionPackages = allCurrentPackages?.validRXPackages.filter(
          (cp) => cp.lensPackage.transition === valueTransition
        );
        const greyOutTransitionPackages: any =
          allCurrentPackages?.invalidRXPackages.filter(
            (cp) => cp.lensPackage.transition === valueTransition
          );
        const groupedByColor = groupBy(
          transitionPackages?.concat(greyOutTransitionPackages),
          'lensPackage.color'
        );
        if (Object.keys(groupedByColor).length === 1) {
          //there is just on color
          //is photocromic
          ret = true;
          setColor(Object.keys(groupedByColor)[0]);
        }
      }
    }

    return ret;
  };
  if (fromTreatments) {
    return checkFromTreatments();
  } else {
    return checkFromTreatmentsFamily();
  }
};

const filterPackagesByFamily = (
  packages: { validRXPackages: any[]; invalidRXPackages: any[] },
  requiredoptions: string[],
  excludedoptions?: string[]
) => {
  let filteredPackages = packages.validRXPackages;
  let greyOutFilteredPackages = packages.invalidRXPackages;

  if (requiredoptions?.length) {
    filteredPackages = filteredPackages.filter((cp) => {
      let ret = true;
      requiredoptions.forEach((t) => (ret = ret && cp.lensPackage[t] != null));
      if (excludedoptions?.length)
        excludedoptions.forEach(
          (t) => (ret = ret && cp.lensPackage[t] === null)
        );
      return ret;
    });

    greyOutFilteredPackages = greyOutFilteredPackages.filter((cp) => {
      let ret = true;
      requiredoptions.forEach((t) => (ret = ret && cp.lensPackage[t] != null));
      if (excludedoptions?.length)
        excludedoptions.forEach(
          (t) => (ret = ret && cp.lensPackage[t] === null)
        );
      return ret;
    });
  }

  return {
    validRXPackages: filteredPackages,
    invalidRXPackages: greyOutFilteredPackages,
  };
};
const filterTreatmentsByFamily = (
  treatments: any[],
  required: string[],
  additional?: string[]
) => {
  let toFilterData: string[] = [];
  if (required) {
    toFilterData = [...toFilterData, ...required];
  }
  if (additional) {
    toFilterData = [...toFilterData, ...additional];
  }
  if (treatments) {
    return treatments.filter((t) => {
      const key = t.id.split('$')[0];
      return toFilterData.some((f) => f === key);
    });
  }
  return null;
};

export const getTreatments = ({
  content,
  currentStep,
  allCurrentPackages,
  fromTreatments,
  dispatch,
}: {
  content?: Content;
  currentStep: Step;
  allCurrentPackages?: any;
  fromTreatments?: boolean;
  dispatch: any;
}) => {
  let _included: any = [];
  let _treatments: any = [];
  const familyFilter = getFamilyFilter(currentStep);

  if (familyFilter) {
    let forcedTreatments = [];
    if (
      isPhotocromic({
        content,
        allCurrentPackages,
        familyFilter,
        fromTreatments,
        dispatch,
      })
    ) {
      forcedTreatments.push('transition');
    }

    let filteredPackages = filterPackagesByFamily(
      allCurrentPackages,
      familyFilter.value?.required,
      familyFilter.value?.excluded
    );
    let { included, treatments } = loadTreatments(
      content,
      filteredPackages,
      true,
      forcedTreatments
    );
    // setIncluded(included);
    // dispatch(
    //   workflowActions.setCurrentIncluded(
    //     included.map((i) => ({ key: i.id, value: i.name }))
    //   )
    // );
    _included = included;

    const onlyFamilyTreatments = filterTreatmentsByFamily(
      treatments,
      familyFilter.value?.required,
      familyFilter.value?.additional
    );
    // setTreatmentAvaiable(onlyFamilyTreatments);
    // setTreatments(onlyFamilyTreatments);
    _treatments = onlyFamilyTreatments;

    let minTreatmentPrice = minBy(onlyFamilyTreatments, (elem) => {
      //if (isInsuranceEnabled) return elem.insPrice;
      return elem.price - elem.discount;
    });

    if (minTreatmentPrice) {
      let treatmentToSetInRedux = new ParameterWithTitle();
      const idTa = minTreatmentPrice.id.split('$')[0];
      const valueTa = minTreatmentPrice.id.split('$')[1];
      treatmentToSetInRedux.key = idTa;
      treatmentToSetInRedux.value = valueTa;
      treatmentToSetInRedux.title = minTreatmentPrice.title;
      treatmentToSetInRedux.price = minTreatmentPrice.price;
      treatmentToSetInRedux.discount = minTreatmentPrice.discount;
      treatmentToSetInRedux.insPrice = minTreatmentPrice.insPrice;

      //dispatch(workflowActions.setParameterPrice([treatmentToSetInRedux]));
    }
  } else {
    // let { included, treatments } = loadTreatments(
    //   content,
    //   allCurrentPackages,
    //   false,
    //   ["transition"]
    // );
    let { included, treatments } = loadTreatments(
      content,
      allCurrentPackages,
      false
    );
    //setIncluded(included);

    // dispatch(
    //   workflowActions.setCurrentIncluded(
    //     included.map((i) => ({ key: i.id, value: i.name }))
    //   )
    // );
    // setTreatmentAvaiable(treatments);
    // setTreatments(treatments);
    _included = included;
    _treatments = treatments;
  }
  return _treatments;
};

export const loadTreatments = (
  content?: Content,
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  empty?: boolean,
  forceTreatments?: string[] | null,
  ignoreIncluded?: boolean,
  treatmentFamily?: string[],
  labels?: { [key: string]: string }
) => {
  const treatmentsOptions = ['clear', 'blueLight', 'transition'];
  const getOptP = (
    opt: string,
    treatmentName: string,
    currentPackages: LensPackage[],
    disabled?: boolean
  ) => {
    //'antiReflective','blueLight','antiScratch','uvProtection'
    let ret: any = {
      id: opt + '$' + treatmentName,
      title: treatmentName,
      filter: null,
    };
    let treatmentFamilyKeys = content.treatmentFamily
      ? Object?.keys?.(content?.treatmentFamily)
      : [];
    switch (opt) {
      case 'clear':
        let clearKey: any = treatmentFamilyKeys?.find((tf: any) =>
          tf.toLowerCase().includes('clear')
        );
        ret.title =
          content.treatmentFamily &&
          content.treatmentFamily[clearKey] &&
          content.treatmentFamily[clearKey].title
            ? content.treatmentFamily[clearKey].title
            : labels?.clearTitle;
        ret.description =
          content.treatmentFamily &&
          content.treatmentFamily[clearKey] &&
          content.treatmentFamily[clearKey].description
            ? content.treatmentFamily[clearKey].description
            : labels?.clearDescription;
        ret.imageUrl =
          content.treatmentFamily &&
          content.treatmentFamily[clearKey] &&
          content.treatmentFamily[clearKey].imageFileName
            ? content.treatmentFamily[clearKey].imageFileName
            : 'clear-icon';
        ret.contentIdentifier =
          content.treatmentFamily &&
          content.treatmentFamily[clearKey]?.contentIdentifier
            ? content.treatmentFamily[clearKey].contentIdentifier
            : null;
        ret.filter = null;
        ret.priceBadge =
          content.treatmentFamily &&
          content.treatmentFamily[clearKey]?.priceBadge
            ? content.treatmentFamily[clearKey]?.priceBadge
            : null;
        break;
      case 'antiScratch':
        ret.title = 'steps.treatments.extraTreatments.' + opt + '.title';

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';
        ret.priceBadge =
          'steps.treatments.extraTreatments.' + opt + '.priceBadge';
        ret.filter = { antiScratch: true };
        break;
      case 'uvProtection':
        ret.title = 'steps.treatments.extraTreatments.' + opt + '.title';

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';

        ret.filter = { uvProtection: true };
        break;
      case 'blueLight':
        ret.title =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].title
            : '';
        ret.description =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].description
            : '';
        ret.imageUrl =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].imageFileName
            : '';
        ret.textBadge =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].badge
            : null;
        ret.badge =
          content[opt] && content[opt][treatmentName]
            ? getStepBadgeType(content[opt][treatmentName].badge)
            : null;
        ret.priceBadge =
          content[opt] && content[opt][treatmentName]?.priceBadge
            ? content[opt][treatmentName].priceBadge
            : null;
        ret.contentIdentifier =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].contentIdentifier
            : null;
        ret.filter = { blueLight: treatmentName, transition: null };
        break;
      case 'antiReflective':
        ret.title =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].title
            : '';
        ret.description =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].description
            : '';
        ret.imageUrl =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].imageFileName
            : '';
        ret.textBadge =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].badge
            : null;
        ret.badge =
          content[opt] && content[opt][treatmentName]
            ? getStepBadgeType(content[opt][treatmentName].badge)
            : null;
        ret.contentIdentifier =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].contentIdentifier
            : null;
        ret.priceBadge =
          content[opt] && content[opt][treatmentName]?.priceBadge
            ? content[opt][treatmentName].priceBadge
            : null;
        ret.filter = { antiReflective: treatmentName };
        break;
      case 'transition':
        ret.title =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].title
            : '';
        ret.description =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].description
            : '';
        ret.imageUrl =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].imageFileName
            : '';
        ret.textBadge =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].badge
            : null;
        ret.badge =
          content[opt] && content[opt][treatmentName]
            ? getStepBadgeType(content[opt][treatmentName].badge)
            : null;
        ret.contentIdentifier =
          content[opt] && content[opt][treatmentName]
            ? content[opt][treatmentName].contentIdentifier
            : null;
        ret.priceBadge =
          content[opt] && content[opt][treatmentName]?.priceBadge
            ? content[opt][treatmentName].priceBadge
            : null;
        ret.filter = { transition: treatmentName };
        break;
    }

    if (!disabled) {
      const priceObj = getPriceStep(ret.filter, currentPackages);
      const minPackageFilter = getCheapestPackages(
        currentPackages,
        ret.filter
      )?.minPackageFilterPrice;
      if (minPackageFilter) {
        window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
      }
      if (priceObj) {
        ret.price = priceObj.price;
        ret.discount = priceObj.discount;
        ret.insPrice = priceObj.insPrice;
      }
    }

    if (disabled) {
      ret.disabled = true;
    }
    const tkey = ret.id.split('$')[0];

    const treatmentsOptions: any = content[tkey]
      ? Object.values(content[tkey])
      : Object.values({ ret });

    if (treatmentsOptions.length > 1) {
      treatmentsOptions.map((option) => {
        if (option.title === ret.title) {
          ret.sequence = option.sequence;
        }
      });
    } else {
      ret.sequence = 0;
    }
    return ret;
  };
  const rules: any = {
    clear: cleanRule,
    blueLight: blueLightRule,
    transition: transitionRule,
  };
  let treatments: StepCardProps[] = [];
  let included: IncludedTreatment[] = [];
  let localOption = treatmentsOptions;
  if (!currentPackages) {
    return { included, treatments };
  }
  if (treatmentFamily && treatmentFamily.length > 0) {
    localOption = localOption.filter((el) => !treatmentFamily.includes(el));
  }

  localOption.forEach((opt: string) => {
    let packageWithTreatments = !empty
      ? currentPackages.validRXPackages.filter((c: any) => c.lensPackage[opt])
      : currentPackages.validRXPackages;

    let greyOutPackageWithTreatments = !empty
      ? currentPackages.invalidRXPackages.filter((c: any) => c.lensPackage[opt])
      : currentPackages.invalidRXPackages;

    packageWithTreatments = packageWithTreatments.filter(
      (pkg) => !rules[opt] || rules[opt](pkg.lensPackage)
    );

    greyOutPackageWithTreatments = greyOutPackageWithTreatments.filter(
      (pkg) => !rules[opt] || rules[opt](pkg.lensPackage)
    );

    const treatmentsOfPackets = groupBy(
      packageWithTreatments,
      'lensPackage.' + opt
    );
    const greyOutTreatmentsOfPackets = groupBy(
      greyOutPackageWithTreatments,
      'lensPackage.' + opt
    );
    const isforced = forceTreatments && forceTreatments.indexOf(opt) >= 0;
    if (treatmentsOfPackets) {
      const availableValue = Object.keys(treatmentsOfPackets);
      const greyOutAvailableValue = greyOutTreatmentsOfPackets
        ? Object.keys(greyOutTreatmentsOfPackets).filter(
            (top) => !availableValue.find((v) => v === top)
          )
        : [];

      const allAvailableValues = [...availableValue, ...greyOutAvailableValue];

      if (allAvailableValues.length <= 0 && !isforced) {
        //not found
      } else if (
        allAvailableValues.length === 1 &&
        !isforced &&
        !ignoreIncluded
      ) {
        //included
        if (availableValue.length) {
          const mapped = getOptP(
            opt,
            availableValue[0],
            currentPackages.validRXPackages
          );
          included.push({
            name: mapped.title,
            id: opt,
            key: mapped.id.split('$')[1],
          });
        }
      } else {
        //avaiable
        treatments = [
          ...treatments,
          ...availableValue
            .filter(
              (treatmentName) =>
                empty ||
                (content && content[opt] && content[opt][treatmentName])
            )
            .map((treatmentName) =>
              getOptP(opt, treatmentName, currentPackages.validRXPackages)
            ),
          ...greyOutAvailableValue
            .filter(
              (treatmentName) =>
                empty ||
                (content && content[opt] && content[opt][treatmentName])
            )
            .map((treatmentName) =>
              getOptP(
                opt,
                treatmentName,
                currentPackages.invalidRXPackages,
                true
              )
            ),
        ];
      }
    }
  });
  return { included, treatments };
};

//blueLight swatch list
export const getTreatmentsFamilyForCustomSwatch = (
  content?: Content,
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  option?: string,
  labels?: { [key: string]: string }
) => {
  let optionsToCheck = [
    {
      key: 'clear',
      rule: cleanRule,
    },
    {
      key: 'blueLight',
      rule: blueLightRule,
    },
    {
      key: 'transition',
      rule: transitionRule,
    },
    {
      key: 'sun',
      rule: sunRule,
    },
  ];

  if (option) {
    optionsToCheck = optionsToCheck.filter((opt) => opt.key === option);
  }

  const getOptP = (
    opt: string,
    treatmentName: string,
    currentPackages: LensPackage[],
    filteredPackages: LensPackage[]
  ) => {
    let ret: any = {
      id: opt,
      title: treatmentName,
      included: false,
    };
    let priceObj = null;
    let tfOpt: any = null;
    let treatmentFamilyKeys = content?.treatmentFamily
      ? Object?.keys?.(content?.treatmentFamily)
      : [];
    let minPackageFilter = null;

    switch (opt.toLowerCase()) {
      case 'clear':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].title
            ? content?.treatmentFamily['Clear'].title
            : labels?.clearTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].description
            ? content?.treatmentFamily['Clear'].description
            : labels?.clearDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].imageFileName
            ? content?.treatmentFamily['Clear'].imageFileName
            : 'clear-icon';
        break;

      case 'sun':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }
        let sunKey: any = treatmentFamilyKeys.find((tf: any) =>
          tf.toLowerCase().includes('sun')
        );
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey].title
            ? content?.treatmentFamily[sunKey].title
            : labels?.sunTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey].description
            ? content?.treatmentFamily[sunKey].description
            : labels?.sunDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey]?.imageFileName
            ? content?.treatmentFamily[sunKey].imageFileName
            : 'sun-icon';
        ret.contentIdentifier =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey]?.contentIdentifier
            ? content?.treatmentFamily[sunKey].contentIdentifier
            : null;
        ret.filter = null;
        break;
      case 'bluelight':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        tfOpt = filteredPackages[0].lensPackage.treatmentFamily;
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].title
            ? content?.treatmentFamily[tfOpt].title
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? content?.blueLight[
                  Object.keys(content?.blueLight).filter(
                    (key) => key === filteredPackages[0].lensPackage.blueLight
                  )[0]
                ].title
              : labels?.bluelightTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].description
            ? content?.treatmentFamily[tfOpt].description
            : labels?.bluelightDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].imageFileName
            ? content?.treatmentFamily[tfOpt].imageFileName
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? content?.blueLight[
                  Object.keys(content?.blueLight).filter(
                    (key) => key === filteredPackages[0].lensPackage.blueLight
                  )[0]
                ].imageFileName2
              : content?.blueLight[
                    Object.keys(content?.blueLight).filter(
                      (key) => key === filteredPackages[0].lensPackage.blueLight
                    )[0]
                  ].imageFileName1
                ? content?.blueLight[
                    Object.keys(content?.blueLight).filter(
                      (key) => key === filteredPackages[0].lensPackage.blueLight
                    )[0]
                  ].imageFileName1
                : content?.blueLight[
                      Object.keys(content?.blueLight).filter(
                        (key) =>
                          key === filteredPackages[0].lensPackage.blueLight
                      )[0]
                    ].imageFileName
                  ? content?.blueLight[
                      Object.keys(content?.blueLight).filter(
                        (key) =>
                          key === filteredPackages[0].lensPackage.blueLight
                      )[0]
                    ].imageFileName
                  : null;
        ret.textBadge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? content?.treatmentFamily[tfOpt].badge
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? content?.blueLight[
                  Object.keys(content?.blueLight).filter(
                    (key) => key === filteredPackages[0].lensPackage.blueLight
                  )[0]
                ].badge
              : null;
        ret.badge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? getStepBadgeType(content?.treatmentFamily[tfOpt].badge)
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? getStepBadgeType(
                  content?.blueLight[
                    Object.keys(content?.blueLight).filter(
                      (key) => key === filteredPackages[0].lensPackage.blueLight
                    )[0]
                  ].badge
                )
              : null;
        break;
      case 'transition':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }
        tfOpt = filteredPackages[0].lensPackage.treatmentFamily;
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].title
            ? content?.treatmentFamily[tfOpt].title
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? content?.transition[
                  Object.keys(content?.transition).filter(
                    (key) => key === filteredPackages[0].lensPackage.transition
                  )[0]
                ].title
              : labels?.transitionTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].description
            ? content?.treatmentFamily[tfOpt].description
            : labels?.transitionDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].imageFileName2
            ? content?.treatmentFamily[tfOpt].imageFileName2
            : content?.treatmentFamily[tfOpt]?.imageFileName1
              ? content?.treatmentFamily[tfOpt]?.imageFileName1
              : content?.treatmentFamily[tfOpt]?.imageFileName
                ? content?.treatmentFamily[tfOpt]?.imageFileName
                : content?.transition &&
                    Object.keys(content.transition).length !== 0
                  ? content.transition[
                      Object.keys(content.transition).filter(
                        (key) =>
                          key === filteredPackages[0].lensPackage.transition
                      )[0]
                    ].imageFileName
                  : null;
        ret.textBadge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? content?.treatmentFamily[tfOpt].badge
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? content?.transition[
                  Object.keys(content?.transition).filter(
                    (key) => key === filteredPackages[0].lensPackage.transition
                  )[0]
                ].badge
              : null;
        ret.badge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? getStepBadgeType(content?.treatmentFamily[tfOpt].badge)
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? getStepBadgeType(
                  content?.transition[
                    Object.keys(content?.transition).filter(
                      (key) =>
                        key === filteredPackages[0].lensPackage.transition
                    )[0]
                  ].badge
                )
              : null;
        break;
    }
    if (priceObj) {
      ret.price = priceObj.price;
      ret.discount = priceObj.discount;
      ret.insPrice = priceObj.insPrice;
    }
    return ret;
  };
  let ret: any = [];
  const sunCheck = [];
  optionsToCheck.forEach((option) => {
    const found = currentPackages.validRXPackages.filter((cp) =>
      option.rule(cp.lensPackage)
    );
    const greyOutFound = currentPackages.invalidRXPackages.filter((cp) =>
      option.rule(cp.lensPackage)
    );
    if ((found && found.length) || (greyOutFound && greyOutFound.length)) {
      if (option.key === 'sun') {
        sunCheck.push(found);
      }
      let thereAreNonIncludedOptions =
        Object.keys(groupBy(found, 'lensPackage.' + option.key)).length > 1;
      //get all unique treatment for packages that have a family
      let treatment: any = [];
      found.map((el: any) => {
        if (
          el.lensPackage.treatmentFamily &&
          !treatment.includes(el.lensPackage[option.key])
        ) {
          treatment.push(el.lensPackage[option.key]);
        }
      });
      //if are more than one (therefore the pachages are more then one) then set areNonIncludedOptions = false
      if (
        thereAreNonIncludedOptions &&
        content?.treatmentFamily &&
        treatment.length > 1
      ) {
        thereAreNonIncludedOptions = false;
      }
      //
      let tFamilyOption =
        found && found.length
          ? getOptP(
              option.key,
              option.key,
              currentPackages.validRXPackages,
              found
            )
          : null;
      let greyOutTfamilyOption =
        greyOutFound && greyOutFound.length
          ? getOptP(
              option.key,
              option.key,
              currentPackages.invalidRXPackages,
              greyOutFound
            )
          : null;

      if (greyOutTfamilyOption) {
        greyOutTfamilyOption['disabled'] = true;
      }

      if (thereAreNonIncludedOptions && tFamilyOption) {
        tFamilyOption.thereAreNonIncludedOptions = thereAreNonIncludedOptions;
      }
      if (tFamilyOption) {
        ret.push(tFamilyOption);
      }

      if (
        (greyOutTfamilyOption && !tFamilyOption) ||
        (greyOutTfamilyOption && greyOutTfamilyOption.id !== tFamilyOption.id)
      ) {
        ret.push(greyOutTfamilyOption);
      }
    }
  });
  return ret;
};

// gvp treatments
export const getGVPTreatments = ({
  content,
  currentPackages,
}: {
  content?: Content;
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
}) => {
  const configGVPTreatment = getContentOptions(content, 'treatment');

  let treatments = configGVPTreatment?.filter((c) =>
    currentPackages?.validRXPackages.some(
      (cp) => cp.lensPackage.treatment === c.id && cp.frame.promoType === 'GVP'
    )
  );
  let treatmentsGreyOut = configGVPTreatment
    ?.filter((c) =>
      currentPackages?.invalidRXPackages.some(
        (cp) =>
          cp.lensPackage.treatment === c.id && cp.frame.promoType === 'GVP'
      )
    )
    ?.map((designType) => {
      return {
        ...designType,
        disabled: true,
      };
    });
  treatments
    ?.concat(treatmentsGreyOut)
    ?.sort((a, b) =>
      a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
    );
  return treatments?.map((c) => {
    const priceObj = c.disabled
      ? null
      : getPriceStep({ treatment: c.id }, currentPackages?.validRXPackages);
    const minPackageFilter = getCheapestPackages(
      currentPackages?.validRXPackages || [],
      { treatment: c.id }
    )?.minPackageFilterPrice;
    if (minPackageFilter) {
      window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
    }

    const filteredPackages = currentPackages?.validRXPackages?.filter((s) =>
      filterExistingProperties(s.lensPackage, { treatment: c.id })
    );
    const speedyRxNotCompatible = filteredPackages?.every(
      (p) => !p.lensPackage.isSpeedyRX
    );

    return {
      ...c,
      title: c.title,
      textBadge: c.badge,
      imageUrl: c.imageFileName,
      price:
        priceObj?.price + parseFloat(minPackageFilter.frame.offerPrice) ||
        priceObj?.price == 0
          ? priceObj?.price + parseFloat(minPackageFilter.frame.offerPrice)
          : null,
      discount:
        priceObj?.discount || priceObj?.discount == 0
          ? priceObj?.discount
          : null,
      insPrice:
        priceObj?.insPrice || priceObj?.insPrice == 0
          ? priceObj?.insPrice
          : null,
      badge: c.badge ? getStepBadgeType(c.badge) : null,
      disabled: c.disabled || c.badge == 'coming soon',
      speedyRxNotCompatible: speedyRxNotCompatible,
    };
  });
};

// treatments family
export const getTreatmentsFamily = (
  content?: Content,
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  option?: string,
  labels?: { [key: string]: string }
) => {
  const mapperMoreInfo = useMoreInfo('treatmentsFamily', content);
  let optionsToCheck = [
    {
      key: 'clear',
      rule: cleanRule,
    },
    {
      key: 'blueLight',
      rule: blueLightRule,
    },
    {
      key: 'transition',
      rule: transitionRule,
    },
    {
      key: 'sun',
      rule: sunRule,
    },
  ];

  if (option) {
    optionsToCheck = optionsToCheck.filter((opt) => opt.key === option);
  }

  const getOptP = (
    opt: string,
    treatmentName: string,
    currentPackages: LensPackage[],
    filteredPackages: LensPackage[]
  ) => {
    if (opt?.toLowerCase()?.includes('blue')) {
      opt = 'blueLight';
    }
    let ret: any = {
      id: opt,
      title: treatmentName,
      included: false,
    };
    let priceObj = null;
    let tfOpt: any = null;
    let treatmentFamilyKeys = content?.treatmentFamily
      ? Object?.keys?.(content?.treatmentFamily)
      : [];
    let minPackageFilter = null;
    let speedyRxNotCompatible = null;

    switch (opt.toLowerCase()) {
      case 'clear':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }

        speedyRxNotCompatible = filteredPackages?.every(
          (p) => !p.lensPackage.isSpeedyRX
        );

        ret.speedyRxNotCompatible = speedyRxNotCompatible;

        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].title
            ? content?.treatmentFamily['Clear'].title
            : labels?.clearTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].description
            ? content?.treatmentFamily['Clear'].description
            : labels?.clearDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily['Clear'] &&
          content?.treatmentFamily['Clear'].imageFileName
            ? content?.treatmentFamily['Clear'].imageFileName
            : 'clear-icon';

        break;

      case 'sun':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }
        speedyRxNotCompatible = filteredPackages?.every(
          (p) => !p.lensPackage.isSpeedyRX
        );

        ret.speedyRxNotCompatible = speedyRxNotCompatible;

        let sunKey: any = treatmentFamilyKeys.find((tf: any) =>
          tf.toLowerCase().includes('sun')
        );
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey].title
            ? content?.treatmentFamily[sunKey].title
            : labels?.sunTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey].description
            ? content?.treatmentFamily[sunKey].description
            : labels?.sunDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey] &&
          content?.treatmentFamily[sunKey]?.imageFileName
            ? content?.treatmentFamily[sunKey].imageFileName
            : 'sun-icon';
        ret.contentIdentifier =
          content?.treatmentFamily &&
          content?.treatmentFamily[sunKey]?.contentIdentifier
            ? content?.treatmentFamily[sunKey].contentIdentifier
            : null;
        ret.filter = null;
        break;
      case 'bluelight':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);

        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
        }

        speedyRxNotCompatible = filteredPackages?.every(
          (p) => !p.lensPackage.isSpeedyRX
        );

        ret.speedyRxNotCompatible = speedyRxNotCompatible;

        tfOpt = filteredPackages[0].lensPackage.treatmentFamily;
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].title
            ? content?.treatmentFamily[tfOpt].title
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? content?.blueLight[
                  Object.keys(content?.blueLight).filter(
                    (key) => key === filteredPackages[0].lensPackage.blueLight
                  )[0]
                ].title
              : labels?.bluelightTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].description
            ? content?.treatmentFamily[tfOpt].description
            : labels?.bluelightDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].imageFileName2
            ? content?.treatmentFamily?.[tfOpt].imageFileName2
            : content?.treatmentFamily?.[tfOpt]?.imageFileName1
              ? content?.treatmentFamily?.[tfOpt]?.imageFileName1
              : content?.treatmentFamily?.[tfOpt]?.imageFileName
                ? content?.treatmentFamily?.[tfOpt]?.imageFileName
                : content?.blueLight &&
                    Object.keys(content?.blueLight).length !== 0
                  ? content?.blueLight[
                      Object.keys(content?.blueLight).filter(
                        (key) =>
                          key === filteredPackages[0].lensPackage.blueLight
                      )[0]
                    ].imageFileName
                  : null;
        ret.textBadge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? content?.treatmentFamily[tfOpt].badge
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? content?.blueLight[
                  Object.keys(content?.blueLight).filter(
                    (key) => key === filteredPackages[0].lensPackage.blueLight
                  )[0]
                ].badge
              : null;
        ret.badge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? getStepBadgeType(content?.treatmentFamily[tfOpt].badge)
            : content?.blueLight && Object.keys(content?.blueLight).length !== 0
              ? getStepBadgeType(
                  content?.blueLight[
                    Object.keys(content?.blueLight).filter(
                      (key) => key === filteredPackages[0].lensPackage.blueLight
                    )[0]
                  ].badge
                )
              : null;
        break;
      case 'transition':
        priceObj = getPriceFromPackageAndCustomFiltered(
          currentPackages,
          filteredPackages
        );
        minPackageFilter = getCheapPackageFromList(filteredPackages);
        if (minPackageFilter) {
          window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
          // ret.speedyRxNotCompatible = !minPackageFilter.lensPackage.isSpeedyRX;
        }

        ret.speedyRxNotCompatible = true; // transition not available for speedy

        tfOpt = filteredPackages[0].lensPackage.treatmentFamily;
        ret.title =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].title
            ? content?.treatmentFamily[tfOpt].title
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? content?.transition[
                  Object.keys(content?.transition).filter(
                    (key) => key === filteredPackages[0].lensPackage.transition
                  )[0]
                ].title
              : labels?.transitionTitle;
        ret.description =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].description
            ? content?.treatmentFamily[tfOpt].description
            : labels?.transitionDescription;
        ret.imageUrl =
          content?.treatmentFamily &&
          content?.treatmentFamily[tfOpt] &&
          content?.treatmentFamily[tfOpt].imageFileName2
            ? content?.treatmentFamily?.[tfOpt].imageFileName2
            : content?.treatmentFamily?.[tfOpt]?.imageFileName1
              ? content?.treatmentFamily?.[tfOpt]?.imageFileName1
              : content?.treatmentFamily?.[tfOpt]?.imageFileName
                ? content?.treatmentFamily?.[tfOpt]?.imageFileName
                : content?.transition &&
                    Object.keys(content.transition).length !== 0
                  ? content.transition[
                      Object.keys(content.transition).filter(
                        (key) =>
                          key === filteredPackages[0].lensPackage.transition
                      )[0]
                    ].imageFileName
                  : null;
        ret.textBadge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? content?.treatmentFamily[tfOpt].badge
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? content?.transition[
                  Object.keys(content?.transition).filter(
                    (key) => key === filteredPackages[0].lensPackage.transition
                  )[0]
                ].badge
              : null;
        ret.badge =
          content?.treatmentFamily && content?.treatmentFamily[tfOpt]
            ? getStepBadgeType(content?.treatmentFamily[tfOpt].badge)
            : content?.transition &&
                Object.keys(content?.transition).length !== 0
              ? getStepBadgeType(
                  content?.transition[
                    Object.keys(content?.transition).filter(
                      (key) =>
                        key === filteredPackages[0].lensPackage.transition
                    )[0]
                  ].badge
                )
              : null;
        break;
    }
    if (priceObj) {
      ret.price = priceObj.price;
      ret.discount = priceObj.discount;
      ret.insPrice = priceObj.insPrice;
    }
    return ret;
  };
  let ret: any = [];
  let sortedOptions = [];

  if (!currentPackages) {
    return ret;
  }
  const sunCheck = [];
  optionsToCheck.forEach((option) => {
    const found = currentPackages.validRXPackages.filter((cp) =>
      option.rule(cp.lensPackage)
    );
    const greyOutFound = currentPackages.invalidRXPackages.filter((cp) =>
      option.rule(cp.lensPackage)
    );
    if ((found && found.length) || (greyOutFound && greyOutFound.length)) {
      if (option.key === 'sun') {
        sunCheck.push(found);
      }
      let thereAreNonIncludedOptions =
        Object.keys(groupBy(found, 'lensPackage.' + option.key)).length > 1;
      //get all unique treatment for packages that have a family
      let treatment: any = [];
      found.map((el: any) => {
        if (
          el.lensPackage.treatmentFamily &&
          !treatment.includes(el.lensPackage[option.key])
        ) {
          treatment.push(el.lensPackage[option.key]);
        }
      });
      //if are more than one (therefore the pachages are more then one) then set areNonIncludedOptions = false
      if (
        thereAreNonIncludedOptions &&
        content?.treatmentFamily &&
        treatment.length > 1
      ) {
        thereAreNonIncludedOptions = false;
      }
      //
      let tFamilyOption =
        found && found.length
          ? getOptP(
              option.key,
              option.key,
              currentPackages.validRXPackages,
              found
            )
          : null;
      let greyOutTfamilyOption =
        greyOutFound && greyOutFound.length
          ? getOptP(
              option.key,
              option.key,
              currentPackages.invalidRXPackages,
              greyOutFound
            )
          : null;

      if (greyOutTfamilyOption) {
        greyOutTfamilyOption['disabled'] = true;
      }

      if (thereAreNonIncludedOptions && tFamilyOption) {
        tFamilyOption.thereAreNonIncludedOptions = thereAreNonIncludedOptions;
      }
      if (tFamilyOption) {
        ret.push(tFamilyOption);
      }

      if (
        (greyOutTfamilyOption && !tFamilyOption) ||
        (greyOutTfamilyOption && greyOutTfamilyOption.id !== tFamilyOption.id)
      ) {
        ret.push(greyOutTfamilyOption);
      }
    }
  });

  //RXC shows "Sun" tile always when sun lenses are available in lensesData
  //despite config.treatmentFamily value
  let treatmentFamily: any = [];
  let splitBlueLight = window.rxcBrain.flowDefiningAttributes?.splitBlueLight;
  let isBlue = currentPackages.validRXPackages.filter((el) =>
    blueLightRule(el.lensPackage)
  );
  let isTrans = currentPackages.validRXPackages.filter((el) =>
    transitionRule(el.lensPackage)
  );
  let isSun = currentPackages.validRXPackages.filter((el) =>
    sunRule(el.lensPackage)
  );
  let isClear = currentPackages.validRXPackages.filter((el) =>
    cleanRule(el.lensPackage)
  );
  if (
    content?.treatmentFamily &&
    Object.keys(content?.treatmentFamily).length > 0
  ) {
    treatmentFamily = [
      isBlue.length > 0 &&
      isBlue[0].lensPackage.treatmentFamily &&
      !splitBlueLight
        ? 'blueLight'
        : null,
      isTrans.length > 0 && isTrans[0].lensPackage.treatmentFamily
        ? 'transition'
        : null,
      isClear.length > 0 && !isClear[0].lensPackage.treatmentFamily
        ? 'clear'
        : null,
    ].filter((el) => el !== null);
  }
  if (sunCheck.length > 0) {
    treatmentFamily.push('sun');
  }

  // possible fix
  if (!treatmentFamily.includes('sun')) {
    if (isSun.length > 0) {
      treatmentFamily.push('sun');
    }
  }

  let avaiabletreatments = loadTreatments(
    content,
    currentPackages,
    true,
    null,
    true,
    treatmentFamily,
    labels
  );

  let newTreatments = avaiabletreatments.treatments;
  newTreatments = [
    ...newTreatments,
    ...ret
      .map((r: any) => {
        if (treatmentFamily.includes(r.id)) {
          let value: any = r.id == 'transition' ? isTrans : isBlue;
          return {
            ...r,
            transitionId: r.id + '$' + value?.[0]?.lensPackage[r.id],
          };
        }
      })
      .filter((el: any) => el !== undefined),
  ];

  avaiabletreatments = {
    ...avaiabletreatments,
    treatments: newTreatments,
  };
  const options: any = optionsToCheck.reduce(
    (keys, option) => ({
      ...keys,
      [option.key]: [],
    }),
    {}
  );

  if (avaiabletreatments && avaiabletreatments.treatments) {
    avaiabletreatments.treatments.forEach((t) => {
      //RXPANEL-1817
      const tkey = t?.id?.split('$')[0];
      // const tValue = t.id.split("$")[1];

      options?.[tkey]?.splice(
        sortedIndexBy(options[tkey], t, 'sequence'),
        0,
        t
      );
    });
    sortedOptions = flatten(Object.values(options));
    return mapperMoreInfo(sortedOptions);
  }
};

// transition colors
export const getTransitionColors = ({
  configProjectImage,
  content,
  allCurrentPackages,
  id,
}: {
  configProjectImage?: string;
  content?: Content;
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  id?: string;
}) => {
  const contentColors = getContentOptions(content, 'color');
  const transitionLenses = allCurrentPackages?.validRXPackages?.filter(
    (p) => p.lensPackage.transition?.length
  );
  const firstValidLensPackageTreatment =
    transitionLenses?.[0]?.lensPackage?.transition;
  let currentPackages: LensPackage[] | undefined;
  // if (!id) {
  //   currentPackages = null;
  // }
  if (id?.split('$')?.[1]) {
    currentPackages = allCurrentPackages?.validRXPackages?.filter(
      (p: LensPackage) => p.lensPackage.transition === id.split('$')[1]
    );
  } else if (
    id?.split('$')?.[0] === 'transition' &&
    allCurrentPackages?.validRXPackages?.every(
      (p) =>
        p.lensPackage.type === 'Non_prescription' ||
        transitionLenses?.every(
          (p) => p.lensPackage.transition === firstValidLensPackageTreatment
        )
    )
  ) {
    currentPackages = allCurrentPackages?.validRXPackages;
  }

  if (currentPackages && contentColors) {
    let transitionColors: Option[];
    const colors = contentColors
      .filter((c) =>
        currentPackages?.some(
          (cp) => cp.lensPackage.color === c.id && cp.lensPackage.transition
        )
      )
      .map((color) => {
        const colorPrice = getPriceStep({ color: color.id }, currentPackages);
        const pkgs = currentPackages?.filter(
          (cp) => cp.lensPackage.color === color.id && cp.lensPackage.transition
        );

        const clen = pkgs?.find(({ lensPackage }) => lensPackage.clen)
          ?.lensPackage?.clen;

        const withExtension =
          (color?.imageFileName as string)?.indexOf('.') > 0;
        const extension = withExtension
          ? (color?.imageFileName as string)?.split('.')[1]
          : 'png';

        const src = `${configProjectImage}${color?.imageFileName}${
          withExtension ? '' : `.${extension}`
        }`;

        return {
          ...color,
          ...colorPrice,
          clen: clen,
          swatchImage: src,
        };
      })
      .sort((a, b) => a?.price - b?.price);

    const invalidRXPackagesFilteredOnColors = contentColors
      .filter((c) =>
        currentPackages?.some((cp) => cp.lensPackage.color === c.id)
      )
      .map((color) => {
        return {
          ...color,
          disabled: true,
        };
      });
    const validRXPackagesFilteredOnColors = contentColors
      .filter((c) =>
        currentPackages?.some((cp) => cp.lensPackage.color === c.id)
      )
      .map((color) => {
        return {
          ...color,
          disabled: false,
        };
      });
    const greyOutColors = invalidRXPackagesFilteredOnColors.filter(
      (inv: any) =>
        !validRXPackagesFilteredOnColors.some((val: any) => val.id === inv.id)
    );
    if (greyOutColors.length > 0) {
      let filtered = colors.filter(
        (color: any) =>
          !greyOutColors.some((gcolor: any) => gcolor.id === color.id)
      );
      transitionColors = [...filtered, ...greyOutColors];
    } else {
      transitionColors = [...colors, ...greyOutColors];
    }
    // if (!selectedColor) {
    //   setSelectedColor(colors[0]);
    // }
    return transitionColors;
  }
};
// thickness
export const getThickness = (
  content?: Content,
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  designTypeSelected?: any
) => {
  let thicknessList = null;
  const configThickness = getContentOptions(content, 'thickness');
  const mapperMoreInfo = useMoreInfo('thickness', content);
  const configOptions = configThickness.filter((c) =>
    allCurrentPackages?.validRXPackages.some(
      (cp) => cp.lensPackage.thickness === c.id
    )
  );
  const greyOutConfigOptions = configThickness
    .filter((c) =>
      allCurrentPackages?.invalidRXPackages.some(
        (cp) => cp.lensPackage.thickness === c.id
      )
    )
    .map((th) => {
      return {
        ...th,
        disabled: true,
      };
    })
    .filter((greyOutTh) => !configOptions.find((th) => th.id === greyOutTh.id));

  if (designTypeSelected && designTypeSelected.length) {
    const f = allCurrentPackages?.validRXPackages.filter(
      (cp) => cp.lensPackage.designType === designTypeSelected
    );
    const greyOutF = allCurrentPackages?.invalidRXPackages.filter(
      (cp) => cp.lensPackage.designType === designTypeSelected
    );
    thicknessList = configOptions.filter((c) =>
      f?.some((cp) => cp.lensPackage.thickness === c.id)
    );
    let greyOutThicknessList = greyOutConfigOptions.filter((c) =>
      greyOutF?.some((cp) => cp.lensPackage.thickness === c.id)
    );

    thicknessList = mapperMoreInfo(thicknessList);
    thicknessList = thicknessList.concat(greyOutThicknessList);
  } else {
    thicknessList = configOptions;
    thicknessList = mapperMoreInfo(thicknessList);
    thicknessList = configOptions.concat(greyOutConfigOptions);
    thicknessList = sortingGreyOut(thicknessList);
  }

  const getParameter = (c: any) => {
    if (allCurrentPackages) {
      const filterP: any = {
        thickness: c.id,
        ...(designTypeSelected &&
          designTypeSelected.length && { designType: designTypeSelected }),
      };
      const priceObj = c.disabled
        ? null
        : getPriceStep(filterP, allCurrentPackages.validRXPackages);
      const minPackageFilter = getCheapestPackages(
        allCurrentPackages.validRXPackages,
        filterP
      )?.minPackageFilterPrice;
      if (minPackageFilter) {
        window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
      }
      const filteredPackages = allCurrentPackages?.validRXPackages?.filter(
        (s) => filterExistingProperties(s.lensPackage, filterP)
      );
      const speedyRxNotCompatible = filteredPackages?.every(
        (p) => !p.lensPackage.isSpeedyRX
      );
      return {
        ...c,
        textBadge: c.badge,
        imageUrl: c.imageFileName,
        price: priceObj?.price,
        discount: priceObj?.discount,
        insPrice: priceObj?.insPrice,
        badge: c.badge ? getStepBadgeType(c.badge) : null,
        disabled: c.disabled || c.badge == 'coming soon',
        speedyRxNotCompatible: speedyRxNotCompatible,
        priceBadge: c.priceBadge || null,
        //discountLoading: isLoading(c.id),
      };
    }
  };
  // temporary test
  // thicknessList = configOptions;
  // thicknessList = mapperMoreInfo(thicknessList);
  // thicknessList = configOptions.concat(greyOutConfigOptions as any);
  // thicknessList = sortingGreyOut(thicknessList);

  return thicknessList.map((t: any) => getParameter(t));
};
// addons
export const getAddons = ({
  content,
  allCurrentPackages,
  labels,
}: {
  content?: Content;
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
}) => {
  // let prevAndCurrentParams = workflow.getPrevCurrentParameterStep();

  // if (checkGVP(currentPackages.validRXPackages)) {
  //   workflow.goNextStep(
  //     [prevAndCurrentParams[prevAndCurrentParams.length - 1]],
  //     true
  //   );
  // }
  const moreInfoMapper = useMoreInfo('antiReflective', content);
  const { treatments } = loadAddons(content, allCurrentPackages, true);

  if (!treatments) return [];

  const onlyAntyReflective = treatments.filter(
    (t) => t.id.split('$')[0] === 'antiReflective'
  );
  onlyAntyReflective.sort((a, b) =>
    a.id.split('$')[1] === 'null' ? -1 : b.id.split('$')[1] === 'null' ? 1 : 0
  );

  const addonsAvaiable = onlyAntyReflective.map((t) => {
    const splittedKey = t.id.split('$');
    const value = splittedKey[1];

    if (value === 'null' || value === 'undefined' || value === '') {
      t.title = labels?.proceedAsIs; //replace to label
      t.image = 'imagepath'; //replace to url path
    }
    return t;
  });
  let addOnsWithMoreInfo = moreInfoMapper(addonsAvaiable);
  return addOnsWithMoreInfo;
  //return addonsAvaiable;
};

const loadAddons = (
  configContent?: Content,
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  empty?: boolean
) => {
  const getOptP = (
    opt: string,
    treatmentName: string,
    currentPackages: LensPackage[],
    disabled?: boolean
  ) => {
    //'antiReflective','blueLight','antiScratch','uvProtection'
    let ret: StepCardProps & { filter: any } = {
      id: opt + '$' + treatmentName,
      title: treatmentName,
      filter: null,
    };
    switch (opt) {
      case 'antiScratch':
        ret.title = 'steps.treatments.extraTreatments.' + opt + '.title';

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';

        ret.filter = { antiScratch: true };
        break;
      case 'uvProtection':
        ret.title = 'steps.treatments.extraTreatments.' + opt + '.title';

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';

        ret.filter = { uvProtection: true };
        break;
      case 'blueLight':
        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName
            : '';
        ret.filter = { blueLight: true };
        break;
      case 'antiReflective':
        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName2 ||
              configContent[opt][treatmentName].imageFileName1 ||
              configContent[opt][treatmentName].imageFileName
            : '';
        ret.filter = { antiReflective: treatmentName };
        ret.textBadge =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].badge
            : '';

        ret.badge = configContent?.[opt]?.[treatmentName]?.badge
          ? getStepBadgeType(configContent[opt][treatmentName].badge)
          : null;
        break;
      case 'transition':
        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName
            : '';
        ret.filter = { transition: treatmentName };
        break;
    }

    if (!ret.imageUrl) {
      //put default image
      ret.imageUrl = 'proceed-icon';
    }

    if (disabled) {
      ret.disabled = true;
    }

    if (!disabled) {
      const priceObj = getPriceStep(ret.filter, currentPackages);
      const minPackageFilter = getCheapestPackages(
        currentPackages,
        ret.filter
      )?.minPackageFilterPrice;
      if (minPackageFilter) {
        window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
      }

      const filteredPackages = currentPackages?.filter((s) =>
        filterExistingProperties(s.lensPackage, ret.filter)
      );
      const speedyRxNotCompatible = filteredPackages?.every(
        (p) => !p.lensPackage.isSpeedyRX
      );
      ret.speedyRxNotCompatible = speedyRxNotCompatible;

      if (priceObj) {
        ret.price = priceObj.price;
        ret.discount = priceObj.discount;
        ret.insPrice = priceObj.insPrice;
      } else {
        ret.price = 0;
        ret.discount = 0;
        ret.insPrice = 0;
      }
    }

    return ret;
  };
  let treatments: StepCardProps[] = [];
  let included: IncludedTreatment[] = [];
  if (!currentPackages?.validRXPackages) {
    return { included, treatments };
  }
  const options = ['antiReflective', 'antiScratch'];

  options.forEach((opt: string) => {
    const packageWithTreatments = !empty
      ? currentPackages.validRXPackages.filter((c: any) => c.lensPackage[opt])
      : currentPackages.validRXPackages;
    const greyOutPackageWithTreatments = !empty
      ? currentPackages.invalidRXPackages.filter((c: any) => c.lensPackage[opt])
      : currentPackages.invalidRXPackages;

    const treatmentsOfPackets = groupBy(
      packageWithTreatments,
      'lensPackage.' + opt
    );
    const greyOutTreatmentsOfPackets = groupBy(
      greyOutPackageWithTreatments,
      'lensPackage.' + opt
    );

    if (treatmentsOfPackets) {
      const availableValue = Object.keys(treatmentsOfPackets);
      const greyOutAvailableValue = greyOutTreatmentsOfPackets
        ? Object.keys(greyOutTreatmentsOfPackets).filter(
            (top) => !availableValue.find((v) => v === top)
          )
        : [];

      const allAvailableValues = [...availableValue, ...greyOutAvailableValue];

      if (allAvailableValues.includes('true' || 'false')) {
        if (allAvailableValues.length <= 0) {
          //not found
        } else if (allAvailableValues.length === 1) {
          //included
          if (availableValue.length === 1) {
            const mapped = getOptP(
              opt,
              allAvailableValues[0],
              currentPackages.validRXPackages
            );
            included.push({
              name: mapped.title,
              id: opt,
              key: mapped.id.split('$')[1],
            });
          }
        }
      } else {
        //avaiable
        treatments = [
          ...treatments,
          ...availableValue
            .filter(
              (treatmentName) =>
                empty ||
                (configContent &&
                  configContent[opt] &&
                  configContent[opt][treatmentName])
            )
            .sort((a, b) => {
              if (a == 'null') return -1;
              else if (b == 'null') return 1;
              else {
                return (
                  Number(configContent[opt][a]?.sequence) -
                  Number(configContent[opt][b]?.sequence)
                );
              }
            })
            .map((treatmentName) =>
              getOptP(opt, treatmentName, currentPackages.validRXPackages)
            ),
          ...greyOutAvailableValue
            .filter(
              (treatmentName) =>
                empty ||
                (configContent &&
                  configContent[opt] &&
                  configContent[opt][treatmentName])
            )
            .sort((a, b) => {
              if (a == 'null') return -1;
              else if (b == 'null') return 1;
              else
                return (
                  configContent[opt][a]?.sequence -
                  configContent[opt][b]?.sequence
                );
            })
            .map((treatmentName) =>
              getOptP(
                opt,
                treatmentName,
                currentPackages.invalidRXPackages,
                true
              )
            ),
        ];
      }
    }
  });
  return { included, treatments };
};

// designtype

export const getDesignTypes = (
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  content?: Content
) => {
  const configDesignType = getContentOptions(content, 'designtype');

  let designTypes = configDesignType?.filter((c) =>
    allCurrentPackages?.validRXPackages.some(
      (cp) => cp.lensPackage.designType === c.id
    )
  );
  let designTypesGreyOut = configDesignType
    ?.filter((c) =>
      allCurrentPackages?.invalidRXPackages.some(
        (cp) => cp.lensPackage.designType === c.id
      )
    )
    ?.map((designType) => {
      return {
        ...designType,
        disabled: true,
      };
    });
  designTypes
    ?.concat(designTypesGreyOut)
    ?.sort((a, b) =>
      a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
    );
  return designTypes?.map((c) => {
    const priceObj = c.disabled
      ? null
      : getPriceStep({ designType: c.id }, allCurrentPackages?.validRXPackages);
    const minPackageFilter = getCheapestPackages(
      allCurrentPackages?.validRXPackages || [],
      { designType: c.id }
    )?.minPackageFilterPrice;
    if (minPackageFilter) {
      window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
    }

    const filteredPackages = allCurrentPackages?.validRXPackages?.filter((s) =>
      filterExistingProperties(s.lensPackage, { designType: c.id })
    );
    const speedyRxNotCompatible = filteredPackages?.every(
      (p) => !p.lensPackage.isSpeedyRX
    );

    return {
      ...c,
      // title: checkTileTitlePrefix(config, workflow.currentStep.key)
      //   ? priceObj?.price
      //     ? `${translate("steps.tilesTitlePrefixUpgrade")} ${c.title}`
      //     : `${translate("steps.tilesTitlePrefixProceed")} ${c.title}`
      //   : c.title,
      title: c.title,
      textBadge: c.badge,
      imageUrl: c.imageFileName,
      price: priceObj?.price || priceObj?.price == 0 ? priceObj?.price : null,
      discount:
        priceObj?.discount || priceObj?.discount == 0
          ? priceObj?.discount
          : null,
      insPrice:
        priceObj?.insPrice || priceObj?.insPrice == 0
          ? priceObj?.insPrice
          : null,
      badge: c.badge ? getStepBadgeType(c.badge) : null,
      disabled: c.disabled || c.badge == 'coming soon',
      speedyRxNotCompatible: speedyRxNotCompatible,
    };
  });
};

// get already included treatments
export const getIncluded = ({
  content,
  allCurrentPackages,
  selections,
  labels,
}: {
  content?: Content;
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  selections: Parameter[];
  labels: { [key: string]: string };
}) => {
  const { included, treatments } = loadIncludedTreatments(
    labels,
    selections,
    content,
    allCurrentPackages?.validRXPackages
  );
  const includedTreatment = included.map((treatment) => ({
    id: treatment.id,
    name: treatment.name, //translate(treatment.name),
  }));
  return { includedTreatment, treatments };
};
const loadIncludedTreatments = (
  labels: { [key: string]: string },
  selections?: Parameter[],
  configContent?: Content,
  currentPackages?: LensPackage[],
  empty?: boolean,
  forceTreatments?: string[]
) => {
  const { uvProtection, antiScratch, premium } = labels;
  const options = [
    // 'blueLight',
    'uvProtection',
    'antiScratch',
    'antiReflective',
  ];
  const getOptP = (
    opt: string,
    treatmentName: string,
    currentPackages: any
  ) => {
    //'antiReflective','blueLight','antiScratch','uvProtection'
    let ret: StepCardProps = {
      id: opt + '$' + treatmentName,
      title: treatmentName,
    };
    let priceObj = null;
    let minPackageFilter = null;
    switch (opt) {
      case 'antiScratch':
        priceObj = getPriceStep({ [opt]: true }, currentPackages);
        minPackageFilter = getCheapestPackages(currentPackages, {
          [opt]: true,
        })?.minPackageFilterPrice;
        //ret.title = "steps.treatments.extraTreatments." + opt + ".title";
        ret.title = antiScratch;

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';

        break;
      case 'uvProtection':
        priceObj = getPriceStep({ [opt]: true }, currentPackages);
        minPackageFilter = getCheapestPackages(currentPackages, {
          [opt]: true,
        })?.minPackageFilterPrice;
        //ret.title = "steps.treatments.extraTreatments." + opt + ".title";
        ret.title = uvProtection;

        ret.imageUrl =
          'steps.treatments.extraTreatments.' + opt + '.imageFileName';

        break;
      case 'blueLight':
        priceObj = getPriceStep({ [opt]: treatmentName }, currentPackages);
        minPackageFilter = getCheapestPackages(currentPackages, {
          [opt]: treatmentName,
        })?.minPackageFilterPrice;
        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName
            : '';
        break;
      case 'antiReflective':
        priceObj = getPriceStep({ [opt]: treatmentName }, currentPackages);
        minPackageFilter = getCheapestPackages(currentPackages, {
          [opt]: treatmentName,
        })?.minPackageFilterPrice;
        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName
            : '';
        break;
      case 'transition':
        priceObj = getPriceStep({ [opt]: treatmentName }, currentPackages);
        minPackageFilter = getCheapestPackages(currentPackages, {
          [opt]: treatmentName,
        })?.minPackageFilterPrice;

        ret.title =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].title
            : '';
        ret.description =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].description
            : '';
        ret.imageUrl =
          configContent[opt] && configContent[opt][treatmentName]
            ? configContent[opt][treatmentName].imageFileName
            : '';
        break;
    }
    if (minPackageFilter) {
      window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilter);
    }
    if (priceObj) {
      ret.price = priceObj.price;
      ret.discount = priceObj.discount;
      ret.insPrice = priceObj.insPrice;
    }
    if (ret.title === 'Premium') {
      ret.title = premium;
    }
    return ret;
  };
  let included: IncludedTreatment[] = [];
  let treatments: IncludedTreatment[] = [];

  if (!currentPackages) {
    return { included, treatments };
  }

  options.forEach((opt: string) => {
    const packageWithTreatments = !empty
      ? currentPackages.filter((c: any) => c.lensPackage[opt])
      : currentPackages;
    const treatmentsOfPackets = groupBy(
      packageWithTreatments,
      'lensPackage.' + opt
    );
    const isforced = forceTreatments && forceTreatments.indexOf(opt) >= 0;
    const allPackagesWithTreatment = currentPackages.every(
      (pack: any) => pack.lensPackage[opt]
    );
    if (treatmentsOfPackets && allPackagesWithTreatment) {
      const avaiableValue = Object.keys(treatmentsOfPackets);

      // let selections = workflow.getAllCurrentParameterStep();
      let treatmentSelected = selections?.find(
        (sel: Parameter) => sel.key === opt
      );

      if (avaiableValue.length <= 0 && !isforced) {
        //not found
      } else if (
        avaiableValue.length === 1 &&
        avaiableValue[0] !== 'No' && // if it's No, it means that the option is not available
        !isforced &&
        !treatmentSelected
      ) {
        //included
        if (opt === 'antiReflective') {
          let addOnsSelection = selections?.find((sel) => sel.key === 'addOns');
          if (addOnsSelection) {
            if (
              addOnsSelection.attributes &&
              addOnsSelection.attributes.isPremium
            ) {
              //push the premium treatment
              const mapped = getOptP(opt, avaiableValue[0], currentPackages);
              treatments.push({
                name: mapped.title,
                id: opt,
                key: mapped.id.split('$')[1],
              });

              //push the included standard treatment
              let temp = Object.keys(configContent?.antiReflective);
              let nonPremiumValue = null;
              if (temp && temp.length) {
                let alreadyIn = temp.find((val) => val === avaiableValue[0]);
                if (!alreadyIn) {
                  nonPremiumValue = temp[0];
                }
              }

              if (nonPremiumValue) {
                const mapped = getOptP(opt, nonPremiumValue, currentPackages);
                included.push({
                  name: mapped.title,
                  id: opt,
                  key: mapped.id.split('$')[1],
                });
              }
            } else if (
              addOnsSelection.attributes &&
              !addOnsSelection.attributes.isPremium
            ) {
              //push the standard treatment as included
              const mapped = getOptP(opt, avaiableValue[0], currentPackages);
              included.push({
                name: mapped.title,
                id: opt,
                key: mapped.id.split('$')[1],
              });
            }
          } else {
            const mapped = getOptP(opt, avaiableValue[0], currentPackages);
            included.push({
              name: mapped.title,
              id: opt,
              key: mapped.id.split('$')[1],
            });
          }
        } else {
          const mapped = getOptP(opt, avaiableValue[0], currentPackages);
          included.push({
            name: mapped.title,
            id: opt,
            key: mapped.id.split('$')[1],
          });
        }
      }
    }
  });
  return { included, treatments };
};

// brand
export const getBrands = (
  content?: Content,
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  }
) => {
  const configBrand = getContentOptions(content, 'brand');
  const mapperMoreInfo = useMoreInfo('brand', content);
  let brandsT = configBrand.filter((c) =>
    allCurrentPackages?.validRXPackages.some(
      (cp) => cp.lensPackage.brand === c.id
    )
  );
  let brandsGreyOut: any = configBrand
    .filter((c) =>
      allCurrentPackages?.invalidRXPackages.some(
        (cp) => cp.lensPackage.brand === c.id
      )
    )
    .map((brand) => {
      return {
        ...brand,
        disabled: true,
      };
    })
    .filter((brand) => !brandsT.find((b) => b.id === brand.id));

  return (
    mapperMoreInfo(brandsT)
      .concat(brandsGreyOut)
      //sorting greyout
      .sort((a, b) =>
        a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
      )
      .map((brand) => {
        const priceObj = brand.disabled
          ? null
          : getPriceStep(
              { brand: brand.id },
              allCurrentPackages?.validRXPackages
            );
        const { minPackageFilterPrice } = getCheapestPackages(
          allCurrentPackages?.validRXPackages,
          { brand: brand.id }
        );
        window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilterPrice);

        const filteredPackages = allCurrentPackages?.validRXPackages?.filter(
          (s) => filterExistingProperties(s.lensPackage, { brand: brand.id })
        );
        const speedyRxNotCompatible = filteredPackages?.every(
          (p) => !p.lensPackage.isSpeedyRX
        );
        return {
          ...brand,
          ...priceObj,
          textBadge: brand.badge,
          imageUrl: brand.imageFileName,
          badge: brand.badge ? getStepBadgeType(brand.badge as string) : null,
          disabled: brand.disabled || brand.badge == 'coming soon',
          speedyRxNotCompatible: speedyRxNotCompatible,
          priceBadge: brand.priceBadge || null,
        };
      })
  );
};

// color categories
export const getColorCategories = (
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  },
  content?: Content
) => {
  const configCategory = content?.colorCategory;
  const currentCategories = groupBy(
    allCurrentPackages?.validRXPackages
      .concat(allCurrentPackages?.invalidRXPackages)
      .filter((cp: LensPackage) => cp.lensPackage.colorCategory),
    'lensPackage.colorCategory'
  );
  const categoriesId = Object.keys(currentCategories);
  if (categoriesId.length) {
    let categories = categoriesId.map((key: string) => ({
      id: key,
      title:
        configCategory && configCategory[key] && configCategory[key].title
          ? configCategory[key].title
          : 'steps.lensColor.categories.' + key + '.title',
    }));
    if (categoriesId.length > 1) {
      categories.unshift({
        id: 'All',
        title: 'All',
      });
    }
    return configObjectToSliderObject.adaptList(categories);
  }
};

// get sun colors
export const getSunColors = ({
  currentPackages,
  content,
}: {
  currentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  content?: Content;
}): LensColorModel[] => {
  const configColor: any[] = getContentOptions(content, 'color');
  const colorCategoryContent = content?.colorCategory;
  const temp = currentPackages?.validRXPackages.filter((cp) => {
    const hasColor = cp.lensPackage.color;
    return hasColor;
  });

  const greyOutTemp = currentPackages?.invalidRXPackages
    .filter((cp) => {
      const hasColor = cp.lensPackage.color;
      return hasColor;
    })
    .map((cp) => {
      return {
        ...cp,
        disabled: true,
      };
    });

  const selections = window.rxcBrain.getStepSelections();
  const isBundleActive = !!(
    selections && selections.find((s) => s.key === 'lensBundle')
  );

  return Object.entries(
    groupBy(temp.concat(greyOutTemp), 'lensPackage.colorCategory')
  )
    .map(([key, value]) => {
      if (key != null) {
        let hasPromo = false;
        let color: LensColorModel[] = [];
        if (value) {
          let groupedByColor = groupBy(value, 'lensPackage.color');
          Object.keys(groupedByColor).map((cg) => {
            let cgPackages = groupedByColor[cg];
            let cheapestCgp = getCheapPackageFromList(cgPackages);
            let cheapestPkg = getCheapPackageFromList(
              window.rxcBrain.currentPackages
            );

            if (key === 'null') {
              key = null;
            }
            const prices = getPriceStep(
              { color: cg, colorCategory: key },
              currentPackages?.validRXPackages
            );
            let listPrice = 0;
            let offerPrice = 0;
            let insPrice = 0;

            if (prices) {
              listPrice = prices.price;
              // if (isInsuranceEnabled) {
              //   if (prices.insPrice) {
              //     offerPrice = prices.insPrice;
              //   } else {
              //     offerPrice = prices.price - prices.discount;
              //   }
              // } else {
              //   offerPrice = prices.price - prices.discount;
              // }
              offerPrice = prices.price - prices.discount;
              insPrice = prices.insPrice;
            }

            const colorName = cheapestCgp.lensPackage.color;
            let recUse = cheapestCgp.lensPackage.recommendedUse;
            let promo: LensColorPromo[] = [];
            // const promoFrame = plainLensColorPromoAdapter.adapt(
            //   cheapestCgp.frame.promo
            // );
            // const lensPromo = plainLensColorPromoAdapter.adapt(
            //   cheapestCgp.lensPackage.promo
            // );
            // if (promoFrame) {
            //   promo = [...promo, ...promoFrame];
            //   hasPromo = true;
            // }
            // if (lensPromo) {
            //   promo = [...promo, ...lensPromo];
            //   hasPromo = true;
            // }
            const configColorp = configColor?.find((c) => c.id === colorName);
            // const defSelected =
            //   lens &&
            //   lens.clen &&
            //   cheapestCgp.clen &&
            //   lens.clen === cheapestCgp.clen;
            if (configColorp) {
              const imageFileName = configColorp?.imageFileName1
                ? configColorp?.imageFileName1
                : configColorp.imageFileName;
              color.push({
                ...configColorp,
                id: configColorp.id,
                name: configColorp.title,
                value: imageFileName,
                promo: promo.length ? promo : null,
                // selected: defSelected ? defSelected : false,
                selected: false,
                price: !isBundleActive
                  ? listPrice
                  : offerPrice -
                    parseFloat(
                      cheapestPkg.lensPackage.offerPrice ??
                        cheapestPkg.lensPackage.listPrice
                    ),
                discount: !isBundleActive ? listPrice - offerPrice : 0,
                insPrice: insPrice,
                recommendedUse: recUse,
                colorCategory: key,
                colorCategoryTitle:
                  colorCategoryContent && colorCategoryContent[key]
                    ? colorCategoryContent[key].title
                    : key,
                disabled: cheapestCgp.lensPackage?.disabled,
                clen: cheapestCgp.lensPackage?.clen,
                promoBadge: configColorp.promoBadge,
              });
            }
          });
        }
        return { name: key, colors: color, hasPromo };
      }
    })
    .reduce((acc, obj) => {
      return [...acc, ...obj.colors];
    }, []);
};

//get generic
export const getOptionsByType = (
  option: string,
  content?: Content,
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  }
) => {
  const configOption = getContentOptions(content, option);
  const mapperMoreInfo = useMoreInfo(option, content);
  let optionsT = configOption.filter((c) =>
    allCurrentPackages?.validRXPackages.some(
      (cp) => cp.lensPackage[option] === parseOptionValue(c?.id)
    )
  );
  let optionsGreyOut: any = configOption
    .filter((c) =>
      allCurrentPackages?.invalidRXPackages.some(
        (cp) => cp.lensPackage[option] === parseOptionValue(c?.id)
      )
    )
    .map((optionItem) => {
      return {
        ...optionItem,
        disabled: true,
      };
    })
    .filter((optionItem) => !optionsT.find((b) => b.id === optionItem.id));

  return mapperMoreInfo(optionsT)
    .concat(optionsGreyOut)
    .sort((a, b) =>
      a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0
    )
    .map((optionItem) => {
      const priceObj = optionItem.disabled
        ? null
        : getPriceStep(
            { [option]: parseOptionValue(optionItem.id) },
            allCurrentPackages?.validRXPackages
          );
      const { minPackageFilterPrice } = getCheapestPackages(
        allCurrentPackages?.validRXPackages,
        {
          [option]: parseOptionValue(optionItem.id),
        }
      );
      window.rxcBrain.setCurrentStepCheapestPackages(minPackageFilterPrice);
      return {
        ...optionItem,
        ...priceObj,
        textBadge: optionItem.badge,
        imageUrl: optionItem.imageFileName,
        badge: optionItem.badge
          ? getStepBadgeType(optionItem.badge as string)
          : null,
        disabled: optionItem.disabled || optionItem.badge == 'coming soon',
      };
    });
};
